import React from "react";

import cx from "classnames";

import { Flex } from "../Flex";
import { Spacer } from "../Grid";
import { Icon } from "../Icon";
import { IconDictionary } from "../Icon/types";
import { Loader } from "../Loader";

import styles from "./Status.scss";

/**
 * Possible variant values for the Status component
 */
export type VariantType =
  | "blue"
  | "gray"
  | "green"
  | "none"
  | "red"
  | "yellow"
  | "transparent"
  | "spinner";

export const Variants: VariantType[] = [
  "blue",
  "gray",
  "green",
  "none",
  "red",
  "yellow",
  "transparent",
  "spinner",
];

/** Interface for Status component properties. */
export interface StatusProps {
  /** Label that represents the status */
  statusLabel?: string;
  /** Label that represents a little note, next to the statusLabel */
  note?: string;
  /** Type of the status circle */
  variant?: VariantType | (string & {});
  /** ClassName prop of the label */
  className?: string;
  /** ClassName prop of the note (e.g., due Dec 31) */
  noteClassName?: string;
  /** A test ID for the div */
  "data-testid"?: string;
  /** Size for Status circle */
  size?: number;
  /** An icon to appear inside the Status circle */
  icon?: IconDictionary;
  /** An icon variant to use for the icon */
  iconVariant?: "regular" | "filled" | "thin";
  /** Size for the icon */
  iconSize?: number;
}

/**
 * Status component displays a circle representing the status before its children.
 */
export function Status({
  variant = "gray",
  statusLabel,
  note,
  className,
  noteClassName,
  children,
  size = 12,
  icon,
  iconVariant,
  iconSize = 10,
  "data-testid": testId,
}: React.PropsWithChildren<StatusProps>) {
  const textToDisplay = React.useMemo(() => {
    if (!note && !statusLabel) {
      return children;
    } else if (statusLabel && !note) {
      return statusLabel;
    } else if (!statusLabel && note) {
      return note;
    }
    return (
      <>
        <b>{statusLabel}</b>
        <span>
          ,{" "}
          <span data-testid="statusNote" className={noteClassName}>
            {note}
          </span>
        </span>
      </>
    );
  }, [note, statusLabel, noteClassName]);

  const dimensions = {
    width: size,
    height: size,
    minWidth: size,
    maxHeight: size,
    borderRadius: size,
  };

  return (
    <Flex
      direction="row"
      alignItems="center"
      className={cx(styles["container"], className, {
        [styles["container--with-note"]]: Boolean(statusLabel && note),
      })}
      testId={testId}
    >
      <Flex
        style={dimensions}
        className={getStatusColorClass(variant)}
        alignItems="center"
        justifyContent="center"
      >
        {icon && (
          <Icon
            icon={icon}
            size={iconSize}
            color="white"
            variant={iconVariant}
          />
        )}
        {variant === "spinner" && (
          <Loader size="small" testId="status-spinner" />
        )}
      </Flex>
      <Spacer size="small" orientation="horizontal" />
      {textToDisplay}
    </Flex>
  );
}

/**
 * Get the CSS class for a given variant.
 */
function getStatusColorClass(variant: string) {
  return cx(styles.status, {
    [styles["status--green"]]: variant === "green",
    [styles["status--yellow"]]: variant === "yellow",
    [styles["status--gray"]]: variant === "gray",
    [styles["status--red"]]: variant === "red",
    [styles["status--blue"]]: variant === "blue",
    [styles["status--none"]]: variant === "none",
    [styles["status--transparent"]]:
      variant === "transparent" || variant === "spinner",
  });
}
